import type { FocusEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Grid } from '@interstate/components/Grid';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@interstate/components/NumericInput';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { kebabCase } from '@makemydeal/dr-common-utils';

import type { FilterItem, FilterItemValue, InputFilterGroup } from '../../types';
import { FocusContainer } from './VehicleSearchRangeFilterInput.style';

const INPUT_MAX_LENGTH = 10;
const DEFAULT_MIN_MAX_DIFF = 5000;

interface VehicleSearchRangeFilterInputProps {
    filterGroup: InputFilterGroup;
    onChange: (appliedFilter: FilterItem, value: FilterItemValue) => void;
    isMaxFiltersReached?: boolean;
}

const VehicleSearchRangeFilterInput = ({
    filterGroup,
    isMaxFiltersReached = false,
    onChange
}: VehicleSearchRangeFilterInputProps) => {
    const [minItem, maxItem] = filterGroup.items;

    const [minValue, setMinValue] = useState(minItem.value);
    const [maxValue, setMaxValue] = useState(maxItem.value);

    // Track pending changes to apply when focus leaves both inputs
    const pendingMinChange = useRef<{ item: FilterItem; value: string } | null>(null);
    const pendingMaxChange = useRef<{ item: FilterItem; value: string } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMinChange = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        setMinValue(event.target.value);
    };

    const handleMaxChange = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        setMaxValue(event.target.value);
    };

    const getHasError = (minValue: string, maxValue: string) => {
        const numericMin = Number.parseInt(minValue);
        const numericMax = Number.parseInt(maxValue);
        const hasMinItem = Number.isFinite(numericMin);
        const hasMaxItem = Number.isFinite(numericMin);
        const hasBothBounds = hasMinItem && hasMaxItem;

        return hasBothBounds && numericMin > numericMax;
    };

    const applyPendingChanges = () => {
        if (pendingMinChange.current) {
            onChange(pendingMinChange.current.item, pendingMinChange.current.value);
            pendingMinChange.current = null;
        }
        if (pendingMaxChange.current) {
            onChange(pendingMaxChange.current.item, pendingMaxChange.current.value);
            pendingMaxChange.current = null;
        }
    };

    const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node | null)) {
            applyPendingChanges();
        }
    };

    const handleMinApply = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        let newMinValue = event.target.value;

        // Use local maxValue state for validation (more accurate during batching flow)
        if (getHasError(newMinValue, maxValue)) {
            const maxNumeric = Number.parseInt(maxValue);
            const defaultValue = maxNumeric - DEFAULT_MIN_MAX_DIFF;
            newMinValue = defaultValue >= 0 ? String(defaultValue) : '0';
            setMinValue(newMinValue);
        }

        pendingMinChange.current = { item: minItem, value: newMinValue };
    };

    const handleMaxApply = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        let newMaxValue = event.target.value;

        // Use local minValue state for validation (more accurate during batching flow)
        if (getHasError(minValue, newMaxValue)) {
            const minNumeric = Number.parseInt(minValue);
            const defaultValue = String(minNumeric + DEFAULT_MIN_MAX_DIFF);
            const maxAllowed = '9'.repeat(INPUT_MAX_LENGTH);
            newMaxValue = defaultValue.length <= INPUT_MAX_LENGTH ? defaultValue : maxAllowed;
            setMaxValue(newMaxValue);
        }

        pendingMaxChange.current = { item: maxItem, value: newMaxValue };
    };

    useEffect(() => {
        setMinValue(minItem.value);
        setMaxValue(maxItem.value);
    }, [minItem, maxItem]);

    return (
        <FocusContainer ref={containerRef} onBlur={handleContainerBlur}>
            <Grid xs={6}>
                <NumericInput
                    inputPrefix={filterGroup.prefix}
                    inputSuffix={filterGroup.suffix}
                    label={minItem.label}
                    name={minItem.label}
                    maxLength={INPUT_MAX_LENGTH}
                    value={minValue}
                    disabled={isMaxFiltersReached && !minItem.value}
                    size="medium"
                    data-testid={`filter-input-${kebabCase(filterGroup.title)}-${kebabCase(minItem.label)}`}
                    onChange={handleMinChange}
                    onBlur={handleMinApply}
                />
            </Grid>
            <Grid xs={6}>
                <NumericInput
                    inputPrefix={filterGroup.prefix}
                    inputSuffix={filterGroup.suffix}
                    label={maxItem.label}
                    name={maxItem.label}
                    maxLength={INPUT_MAX_LENGTH}
                    value={maxValue}
                    disabled={isMaxFiltersReached && !maxItem.value}
                    size="medium"
                    data-testid={`filter-input-${kebabCase(filterGroup.title)}-${kebabCase(maxItem.label)}`}
                    onChange={handleMaxChange}
                    onBlur={handleMaxApply}
                />
            </Grid>
        </FocusContainer>
    );
};

export default VehicleSearchRangeFilterInput;
